import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { colors } from 'src/theme';
import {
  i18n,
  useSessionStorage,
  showMessage,
  handleErrorMessage,
  useGeneralSoContext,
  openCallMeModal,
  PropertyBrandWithProducts,
  SELECTED_BRAND_KEY,
  SELECTED_PRODUCT_KEY,
  INSURANCE_NEEDS_KEY,
  CUSTOMER_DATA_KEY,
  PROPERTY_INFO_KEY,
  SoPropertyInsuranceNeedsInput,
  SoPropertyInfoInput,
  SoDataInput,
  useCreateSo,
  useCalculationInput,
} from 'src/common';
import { Button, CheckIcon, CloseIcon, ContainerComponent, Flex, InsuranceNeedsModal, LoaderSquare, RequestAgentCardV2, StepHeader, Tag, Text } from 'src/components';
import { InsuranceNeedsFormData } from 'src/components/forms/insuranceNeeds.form';
import { OfferCard } from 'src/components/cards/offer.card';
import { NavigationService } from 'src/common/utils/navigation.util';
import { AppRoutes } from './_router/app.routes';
import { useGetSo } from 'src/common/api/apollo/queries/useGetSo.query';
import { useGetCalculations } from 'src/common/api/apollo/queries/useGetCalculations.query';
import { Helmet } from 'react-helmet-async';

export const OffersWebshopPage: React.FC = () => {
  const { so, loading: soLoading, refetch } = useGetSo();
  const { soId, soInitialData, setSoInitialData } = useGeneralSoContext();
  const { setValue: setSelectedOffer } = useSessionStorage<{ code: string, name: string }>(SELECTED_BRAND_KEY);
  const { getValue: getSelectedProduct, setValue: setSelectedProduct } = useSessionStorage<{ brandCode: string, code: string, name: string }[]>(SELECTED_PRODUCT_KEY);
  const { setValue: setInsuranceNeeds, getValue: getInsuranceNeeds } = useSessionStorage<SoPropertyInsuranceNeedsInput>(INSURANCE_NEEDS_KEY);
  const { getValue: getPropertyInfo } = useSessionStorage<SoPropertyInfoInput>(PROPERTY_INFO_KEY);
  const { getValue: getCustomerData } = useSessionStorage<SoDataInput>(CUSTOMER_DATA_KEY);
  const { handleInsuranceNeeds, additionalCoverages } = useCalculationInput();
  const [createSo] = useCreateSo();
  const [insuranceNeedsModalOpen, setInsuranceNeedsModalOpen] = useState(false);

  useEffect(() => {
    refetch()
  }, []);

  useEffect(() => {
    if (!soInitialData && so?.data && so?.productTypeSpecific?.propertyInfo) {
      setSoInitialData({ data: so.data, propertyInfo: so.productTypeSpecific?.propertyInfo });
    }
  }, [so])

  const [subscriptionId] = useState(() => uuidv4());
  const { calculation, loadingBrands, loading: calculationsLoading, error } = useGetCalculations(subscriptionId);

  React.useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        message: handleErrorMessage(error),
        duration: 5000,
        fixed: true,
      });
    }
  }, [error]);

  const handleSelectCalc = (calc: PropertyBrandWithProducts) => {
    const selectedProducts = getSelectedProduct() || [];
    setSelectedOffer({ code: calc.code, name: calc.name });
    const selProd = selectedProducts?.find(prod => prod.brandCode === calc.code);

    if (!selProd) {
      selectedProducts.push({ brandCode: calc.code, code: calc.products[0]?.code, name: calc.products[0]?.name });
      setSelectedProduct(selectedProducts);
    }

    if (calc.products?.[0].calculation) {
      NavigationService.navigate(AppRoutes.WEBSHOP.OFFER);
    } else {
      NavigationService.navigate(AppRoutes.FUNNEL.CONTACT);
    }
  };

  const onBack = () => {
    NavigationService.navigate(AppRoutes.WEBSHOP.RISKS);
  }

  const [selectedRisks, setSelectedRisks] = useState<string[]>(
    () => getInsuranceNeeds()?.risks ?? []
  );

  useEffect(() => {
    if (!getInsuranceNeeds() && so?.productTypeSpecific?.insuranceNeeds?.risks) {
      setSelectedRisks(so.productTypeSpecific.insuranceNeeds.risks);
    }
  }, [so]);

  const persistInsuranceNeeds = async (newRisks: string[]): Promise<boolean> => {
    const existing = getInsuranceNeeds() ?? so?.productTypeSpecific?.insuranceNeeds;
    const insuranceNeeds: SoPropertyInsuranceNeedsInput = {
      building: existing?.building ?? false,
      personalProperty: existing?.personalProperty ?? false,
      risks: newRisks,
    };

    setInsuranceNeeds(insuranceNeeds);

    const data = (soInitialData?.data as SoDataInput | undefined) ?? getCustomerData() ?? {};
    const propertyInfo = (soInitialData?.propertyInfo as SoPropertyInfoInput | undefined) ?? getPropertyInfo() ?? undefined;

    try {
      await createSo({
        soId,
        data,
        productTypeSpecific: {
          insuranceNeeds,
          propertyInfo,
        },
      });
      setSelectedRisks(newRisks);
      return true;
    } catch (error) {
      showMessage({
        type: 'error',
        message: handleErrorMessage(error),
        duration: 5000,
        fixed: true,
      });
      return false;
    }
  };

  const handleInsuranceNeedsSubmit = async (values: InsuranceNeedsFormData) => {
    const ok = await persistInsuranceNeeds(values.selectedRiskIds || []);
    if (ok) {
      handleInsuranceNeeds(values.selectedRiskIds || [], true);
      setInsuranceNeedsModalOpen(false)
    };
  };

  const handleRemoveRisk = (riskId: string) => {
    const newRisks = selectedRisks.filter((r) => r !== riskId);
    persistInsuranceNeeds(newRisks);

    if (riskId === 'earthquake' && additionalCoverages.some((aC) => aC.coverageCode === 'earthquakeFranchise')) {
      handleInsuranceNeeds(['earthquakeFranchise'], false);
    } else {
      handleInsuranceNeeds([riskId], false);
    }

  };

  const handleSelect = (brandCode: string, productCode: string) => {
    const brandC = brandsWithProducts?.find(brand => brand.code === brandCode);
    const product = brandC?.products?.find(prod => prod.code === productCode);
    const selectedProducts = getSelectedProduct();
    if (product) {
      const selProd = selectedProducts?.filter(prod => prod.brandCode !== brandCode);
      if (selProd) {
        setSelectedProduct([...selProd, {
          brandCode,
          code: product.code,
          name: product.name
        }]);
      } else {
        setSelectedProduct([{
          brandCode,
          code: product.code,
          name: product.name
        }])
      }
    }
  };

  const showLoading = soLoading || (calculationsLoading && !calculation?.length);

  const brandsWithProducts = React.useMemo(() => {
    const brandsWithSelectedCoverages = new Set(additionalCoverages.map((c) => c.brandCode));

    const rank = (code?: string, actionLabel?: string, hasCalculation?: boolean) => {
      if (code && loadingBrands.has(code)) return 0;
      if (code && brandsWithSelectedCoverages.has(code)) return 1;
      if (hasCalculation) return 2;
      if (actionLabel) return 3;
      return 4;
    };

    return calculation?.slice().sort((a, b) =>
      rank(a.code, a?.products?.[0]?.actionLabel as string, Boolean(a.products?.[0]?.calculation)) - rank(b.code, b?.products?.[0]?.actionLabel as string, Boolean(b.products?.[0]?.calculation)) ||
      (a?.products?.[0]?.calculation?.totalPremium ?? Infinity) -
        (b?.products?.[0]?.calculation?.totalPremium ?? Infinity) ||
      a.code!.localeCompare(b.code!)
    );
  }, [calculation, loadingBrands, additionalCoverages]) as PropertyBrandWithProducts[] | undefined;

  const sessionProducts = getSelectedProduct()?.map(
    (selProd) => brandsWithProducts?.find(
      (bP) => bP.code === selProd.brandCode
    )?.products?.find(
      (bP) => bP.code === selProd.code
  ));

  return (
    <ContainerComponent>
      <Helmet>
        <title>Ponude | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader />
      <Flex vertical justify="center" gap={16}>
      {showLoading ? (
        <Flex vertical align="center" gap={8}>
          <LoaderSquare/>
          <Text type="base" color={colors.text3}>
            {i18n.t('pages.offers.loading')}
          </Text>
        </Flex>
      ) : !calculationsLoading && !brandsWithProducts?.length ? (
        <Text type="base" color={colors.text3}>
          {i18n.t('pages.offers.noOffers')}
        </Text>
      ) : (
        <Flex vertical gap={12}>
          <Flex justify='space-between' align="center">
            <Text type="base" >Za dodatnu sigurnost</Text>
            <Button
              size="medium"
              text={i18n.t(`components.forms.insuranceNeeds.add`)}
              btnType="outlinePrimary"
              onClick={() => setInsuranceNeedsModalOpen(true)}
            />
          </Flex>
          <Flex wrap="wrap" gap={4}>
            {selectedRisks.map((risk) => (
              <Tag
                key={risk}
                value={
                  <Flex align="center" justify="center" gap={4} style={{ padding: 4 }}>
                    <CheckIcon width={16} height={16} color={colors.primary} />
                    <Text type="small" color={colors.primary}>
                      {i18n.t(`components.forms.insuranceNeeds.${risk}`)}
                    </Text>
                    <CloseIcon
                      width={16}
                      height={16}
                      color={colors.primary}
                      onClick={() => handleRemoveRisk(risk)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Flex>
                }
                textColor={colors.primary}
                background={colors.primary5}
              />
            ))}
          </Flex>
          {brandsWithProducts?.map((brandWithProduct) => (
            <OfferCard
              key={brandWithProduct.code}
              brandCalc={brandWithProduct}
              brandLoading={loadingBrands.has(brandWithProduct.code)}
              initialProduct={sessionProducts?.find((sP) => sP?.code === brandWithProduct.code) || brandWithProduct.products[0]}
              onSelectOffer={() => handleSelectCalc(brandWithProduct)}
              onSelectPackage={(productCode) => handleSelect(brandWithProduct.code, productCode)}
              calculationsLoading={calculationsLoading}
            />
          ))}
        </Flex>
      )}
      <RequestAgentCardV2
        onButtonClick={openCallMeModal}
      />
        <Button style={{ padding: '44px 0' }} text={i18n.t('common.back')} btnType="textBlack" size="large" onClick={onBack} />
      </Flex>
      <InsuranceNeedsModal
        open={insuranceNeedsModalOpen}
        onClose={() => setInsuranceNeedsModalOpen(false)}
        onSubmit={handleInsuranceNeedsSubmit}
        initialData={{ selectedRiskIds: selectedRisks }}
      />
    </ContainerComponent>
  );
};

export default OffersWebshopPage;
